import { useEffect, useState, type ReactElement } from 'react';
import { api, type MailSettingsView } from '../api';
import { Sheet } from './Sheet';
import { useNachgeliefert } from '../nachgeliefert';

/**
 * The mail relay. Without it there are no invitations and no way back into a
 * forgotten account, so the test button sends a real mail rather than only
 * opening a connection: a relay that accepts the connection and then refuses
 * the sender address says so at the very end.
 */
export function MailSettingsSheet({ onClose }: { onClose: () => void }): ReactElement {
  const [view, setView] = useState<MailSettingsView | null>(null);
  const [form, setForm, formNachliefern] = useNachgeliefert({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromAddress: '',
    fromName: 'SHA TV Schnitt',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const { mail } = await api.mailSettings();
      setView(mail);
      formNachliefern((f) => ({ ...f, ...mail, password: '' }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Die Einstellungen konnten nicht geladen werden.');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const run = async (what: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await what();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Mailversand" onClose={onClose}>
      {error ? <p className="error-text">{error}</p> : null}

      <div className="card" style={{ margin: '0 0 14px' }}>
        <div className="spread">
          <div>
            <div className="title">{view?.configured ? 'Eingerichtet' : 'Noch nicht eingerichtet'}</div>
            <div className="sub">
              {view?.configured ? `Absender ${view.fromAddress}` : 'Ohne Mailversand gibt es keine Einladungen.'}
            </div>
          </div>
          <span className={`badge ${view?.configured ? 'ok' : 'err'}`}>{view?.configured ? 'da' : 'fehlt'}</span>
        </div>
        {view?.unreadable ? (
          <p className="hint" style={{ color: 'var(--err-text)' }}>
            Das gespeicherte Passwort lässt sich nicht mehr entschlüsseln. Bitte neu eintragen.
          </p>
        ) : null}
      </div>

      <label className="field">
        <span>Server</span>
        <input
          value={form.host}
          onChange={(e) => setForm({ ...form, host: e.target.value })}
          placeholder="smtp.example.de"
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Port</span>
        <input
          value={String(form.port)}
          onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 0 })}
          inputMode="numeric"
        />
        <p className="hint">587 ohne SSL (üblich), 465 mit SSL.</p>
      </label>
      <label className="spread" style={{ marginBottom: 12 }}>
        <span className="sub">Verbindung von Anfang an verschlüsselt (SSL, meist Port 465)</span>
        <input
          type="checkbox"
          checked={form.secure}
          onChange={(e) => setForm({ ...form, secure: e.target.checked })}
          style={{ width: 22, height: 22, minHeight: 0 }}
        />
      </label>
      <label className="field">
        <span>Benutzername</span>
        <input
          value={form.user}
          onChange={(e) => setForm({ ...form, user: e.target.value })}
          autoCapitalize="none"
          placeholder="leer lassen, wenn der Server keine Anmeldung verlangt"
        />
      </label>
      <label className="field">
        <span>Passwort</span>
        <input
          type="password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder={view?.hasPassword ? '•••••••• (bleibt, wenn leer)' : ''}
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Absenderadresse</span>
        <input
          type="email"
          value={form.fromAddress}
          onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
          placeholder="schnitt@sha-tv.de"
          autoCapitalize="none"
        />
      </label>
      <label className="field">
        <span>Absendername</span>
        <input value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
      </label>

      {result ? <p className="hint" style={{ color: result.ok ? 'var(--ok-text)' : 'var(--err-text)' }}>{result.message}</p> : null}

      <div className="stack">
        <button disabled={busy} onClick={() => void run(async () => setResult(await api.testMailSettings(form)))}>
          {busy ? 'Sende…' : 'Testmail an mich schicken'}
        </button>
        <button
          className="primary"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              const { mail } = await api.saveMailSettings(form);
              setView(mail);
              setForm({ ...form, password: '' });
              setResult(await api.testMailSettings({}));
            })
          }
        >
          Speichern und Testmail schicken
        </button>
        {view?.configured ? (
          <button
            className="ghost"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api.clearMailSettings();
                setResult(null);
                await load();
              })
            }
          >
            Einstellungen löschen
          </button>
        ) : null}
      </div>
    </Sheet>
  );
}
